import { api } from '@/lib/api'
import type {
  AtsDashboard,
  CargoOpt,
  DepartamentoOpt,
  EmpresaGrupoOpt,
  Paginated,
  PipelineMap,
  Requisicao,
  RequisicaoCreatePayload,
  VagaCreatePayload,
  VagaDetalhe,
  VagaResumo,
} from './types'

function unwrapDados<T>(res: { dados?: T } | T): T {
  if (res && typeof res === 'object' && 'dados' in res && (res as { dados?: T }).dados != null) {
    return (res as { dados: T }).dados
  }
  return res as T
}

export async function fetchAtsDashboard() {
  return api.get<AtsDashboard>('/ats/dashboard/resumo/')
}

export async function fetchVagas(params: {
  status?: string
  search?: string
  page?: number
} = {}) {
  const qs = new URLSearchParams()
  if (params.status) qs.set('status', params.status)
  if (params.search) qs.set('search', params.search)
  if (params.page) qs.set('page', String(params.page))
  const suffix = qs.toString() ? `?${qs}` : ''
  return api.get<Paginated<VagaResumo>>(`/ats/vagas/${suffix}`)
}

export async function fetchVaga(id: string) {
  return api.get<VagaDetalhe>(`/ats/vagas/${id}/`)
}

export async function createVaga(payload: VagaCreatePayload) {
  const res = await api.post<VagaDetalhe | { dados: VagaDetalhe }>('/ats/vagas/', payload)
  return unwrapDados(res)
}

export async function patchVaga(id: string, payload: Partial<VagaCreatePayload>) {
  const res = await api.patch<VagaDetalhe | { dados: VagaDetalhe }>(`/ats/vagas/${id}/`, payload)
  return unwrapDados(res)
}

export async function publicarVaga(id: string) {
  return api.post<{ sucesso?: boolean; status?: string; mensagem?: string }>(
    `/ats/vagas/${id}/publicar/`,
  )
}

export async function pausarVaga(id: string) {
  return api.post<{ sucesso?: boolean; status?: string }>(`/ats/vagas/${id}/pausar/`)
}

export async function fecharVaga(id: string) {
  return api.post<{ sucesso?: boolean; status?: string }>(`/ats/vagas/${id}/fechar/`)
}

export async function fetchPipeline(vagaId: string) {
  return api.get<PipelineMap>(`/ats/vagas/${vagaId}/pipeline/`)
}

export async function moverCandidatura(id: string, etapa: string, motivo = '') {
  return api.post<{ sucesso?: boolean; mensagem?: string }>(
    `/ats/candidaturas/${id}/mover/`,
    { etapa, motivo },
  )
}

export async function fetchRequisicoes(params: {
  status?: string
  search?: string
  page?: number
} = {}) {
  const qs = new URLSearchParams()
  if (params.status) qs.set('status', params.status)
  if (params.search) qs.set('search', params.search)
  if (params.page) qs.set('page', String(params.page))
  const suffix = qs.toString() ? `?${qs}` : ''
  return api.get<Paginated<Requisicao> | { sucesso?: boolean; dados: Requisicao[]; total?: number }>(
    `/ats/requisicoes/${suffix}`,
  )
}

export async function createRequisicao(payload: RequisicaoCreatePayload) {
  const res = await api.post<{ sucesso?: boolean; dados: Requisicao }>('/ats/requisicoes/', payload)
  return unwrapDados(res)
}

export async function aprovarRequisicao(id: string) {
  return api.post<{ sucesso?: boolean; mensagem?: string; dados?: Requisicao }>(
    `/ats/requisicoes/${id}/aprovar/`,
  )
}

export async function rejeitarRequisicao(id: string, motivo: string) {
  return api.post<{ sucesso?: boolean; mensagem?: string }>(`/ats/requisicoes/${id}/rejeitar/`, {
    motivo,
  })
}

export async function solicitarInfoRequisicao(id: string, texto: string) {
  return api.post<{ sucesso?: boolean; mensagem?: string }>(
    `/ats/requisicoes/${id}/solicitar_info/`,
    { texto },
  )
}

export async function fetchEmpresasGrupo() {
  const res = await api.get<{ sucesso?: boolean; dados: EmpresaGrupoOpt[] }>(
    '/ats/empresas-grupo/?completo=1&ativos=1',
  )
  return res.dados || []
}

/** Empresas jurídicas RH (Domínio) — ativas/visíveis, principal primeiro. */
export async function fetchEmpresasRhAtivas() {
  const qs = new URLSearchParams({ status: 'ativo', por_pagina: '200', page: '1' })
  const full = await api.get<{
    sucesso?: boolean
    dados: Array<{
      id: string
      nome: string
      nome_fantasia?: string
      principal?: boolean
      ativo?: boolean
      codigo_dominio?: string
    }>
  }>(`/rh/empresas/?${qs}`)
  const lista = full.dados || []
  return [...lista].sort(
    (a, b) =>
      Number(b.principal) - Number(a.principal) || a.nome.localeCompare(b.nome, 'pt-BR'),
  )
}

export async function fetchCargosOpts() {
  // Contratação: só cargos ativos com colaboradores anexados (headcount > 0)
  const res = await api.get<{ sucesso?: boolean; dados: CargoOpt[] }>(
    '/rh/cargos/?completo=1&com_colaboradores=1',
  )
  return res.dados || []
}

export async function fetchDepartamentosOpts() {
  const res = await api.get<{ sucesso?: boolean; dados: DepartamentoOpt[] }>('/rh/departamentos/')
  return res.dados || []
}
